import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as React from "react";
import Stack from "@mui/material/Stack";
import Sidebar from "../Dashboard/Sidebar.js";
import { Box } from "@mui/system";
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import { ToastContainer, toast } from "react-toastify";
import { Add, DeleteForever, Edit, Refresh } from "@mui/icons-material";

// --- STYLED COMPONENTS ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
});

function InventoryPage() {
  return (
    <RootContainer>
      <Sidebar />
      <InventoryList />
    </RootContainer>
  );
}

const statusColors = {
  available: "success",
  sold: "error",
  reserved: "warning",
};

const InventoryList = () => {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [rowCount, setRowCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize] = React.useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteParams, setDeleteParams] = React.useState(null);
  const navigate = useNavigate();
   const isSuperAdmin = localStorage.getItem("superadmin") === "true";
  const [refreshing, setRefreshing] = React.useState(false);

  const handleRefreshDispatch = async () => {
    setRefreshing(true);
    try {
      await axios.post(
        "https://api.github.com/repos/MdWasimAslam/flywheel-web/dispatches",
        { event_type: "inventory_updated" },
        {
          headers: {
            Accept: "application/vnd.github+json",
            Authorization: "token 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",
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Refresh triggered successfully");
    } catch (error) {
      console.error("Refresh dispatch failed:", error);
      toast.error("Failed to trigger refresh");
    } finally {
      setRefreshing(false);
    }
  };

  // delete inventory handler
  const handleDelete = async (inventoryId, folderName, folderPath) => {
    try {
      let imageDeleteSuccess = true;
      let inventoryDeleteSuccess = false;
      let errorMessage = "Failed to delete inventory";
      const formData = new URLSearchParams();
      formData.append("target", folderName);
      formData.append("path", folderPath);
      const token = localStorage.getItem("accessToken");

      // 1. Only attempt image deletion if we actually have folder context
      if (folderName && folderPath) {
        const imageResponse = await axios.post(
          "https://files.flywheelcars.com/api.php?action=delete",
          formData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        );
        imageDeleteSuccess = imageResponse.data.success;
        if (!imageDeleteSuccess) {
          errorMessage =
            imageResponse.data.message || "Failed to delete images";
        }
      }

      // 2. Delete inventory from database (using the inventoryId)
      if (imageDeleteSuccess) {
        const inventoryResponse = await axios.delete(
          `/delete-inventory/${inventoryId}`,
          {
            headers: {
              auth: token,
            },
          }
        );
        inventoryDeleteSuccess =
          inventoryResponse.data.type === "Success";
        if (!inventoryDeleteSuccess) {
          errorMessage =
            inventoryResponse.data.message ||
            "Failed to delete inventory record";
        }
      }

      // 3. Final status check
      if (imageDeleteSuccess && inventoryDeleteSuccess) {
        toast.success("Inventory deleted successfully");
      } else {
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting inventory:", error);
      toast.error("A network error occurred while deleting.");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteParams(null);
      fetchInventory();
    }
  };

  const handleConfirmDelete = () => {
    if (deleteParams) {
      handleDelete(
        deleteParams.id,
        deleteParams.folderName,
        deleteParams.imagesPath,
      );
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteParams(null);
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            color="primary"
            onClick={() =>
              navigate("/AddCar/" + params.row.id, {
                state: { inventory: params.row },
              })
            }
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#eff6ff" },
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
          {isSuperAdmin && (
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                let folderName = null;
                let imagesPath = null;

                try {
                  // Safety check: Ensure images exist and is a valid string/array
                  const imageLocation =
                    typeof params.row.images === "string"
                      ? JSON.parse(params.row.images)
                      : params.row.images;

                  if (
                    Array.isArray(imageLocation) &&
                    imageLocation.length > 0 &&
                    imageLocation[0]?.url
                  ) {
                    const imageUrl = imageLocation[0].url;
                    const url = new URL(imageUrl);
                    const encodedPath = url.searchParams.get("file");

                    if (encodedPath) {
                      const decodedPath = decodeURIComponent(encodedPath);
                      console.log(decodedPath);
                      const folderPathStr = decodedPath.substring(
                        0,
                        decodedPath.lastIndexOf("/"),
                      );

                      folderName = folderPathStr.split("/").pop(); // The UUID folder name
                      const parts = decodedPath.split("/");
                      imagesPath = parts.slice(0, 3).join("/"); // e.g. "inventory/YYYY/MM"
                      console.log(folderName, "folderName");
                      console.log(imagesPath, "imagesPath");
                    }
                  }
                } catch (e) {
                  console.warn(
                    "Failed to parse image path for deletion, skipping image file deletion",
                    e,
                  );
                }

                // Pass ID and File context to the handler
                setDeleteParams({
                  id: params.row.id,
                  folderName: folderName,
                  imagesPath: imagesPath,
                });
                setDeleteDialogOpen(true);
              }}
              sx={{
                border: "1px solid",
                borderColor: "error.main",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#ffe6e6" },
              }}
            >
              <DeleteForever fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ),
    },
    {
      field: "image",
      headerName: "Image",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        let src = "";
        try {
          const images =
            typeof params.row.images === "string"
              ? JSON.parse(params.row.images)
              : params.row.images;
          if (Array.isArray(images) && images.length > 0) {
            src = images[0].variants["400"];
          }
        } catch {}
        return (
          <Avatar variant="rounded" src={src} sx={{ width: 48, height: 48 }} />
        );
      },
    },
    {
      field: "car_name",
      headerName: "Car Name",
      width: 250,
    },
    {
      field: "brand",
      headerName: "Brand",
      width: 120,
    },
    {
      field: "body_type",
      headerName: "Body Type",
      width: 110,
    },
    {
      field: "transmission",
      headerName: "Transmission",
      width: 120,
    },
    {
      field: "fuel_type",
      headerName: "Fuel",
      width: 100,
    },
    {
      field: "driven",
      headerName: "Driven (km)",
      width: 110,
      type: "number",
      valueFormatter: (params) => {
        const val = params.value;
        return val != null ? Number(val).toLocaleString() : "-";
      },
    },
    {
      field: "color",
      headerName: "Color",
      width: 100,
    },
    {
      field: "registration",
      headerName: "Reg. Year",
      width: 100,
      type: "number",
    },
    {
      field: "price",
      headerName: "Price (₹)",
      width: 130,
      type: "number",
      valueFormatter: (params) => {
        const val = params.value;
        return val != null ? `₹${Number(val).toLocaleString("en-IN")}` : "-";
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value || "N/A"}
          color={statusColors[params.value] || "default"}
          size="small"
          sx={{ fontWeight: 600, textTransform: "capitalize" }}
        />
      ),
    },
    {
      field: "views_count",
      headerName: "Views",
      width: 80,
      type: "number",
    },
  ];

  const fetchInventory = React.useCallback(async () => {
    setIsLoading(true);
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.get("/get-inventory", {
        params: {
          page: page + 1,
          limit: pageSize,
        },
        headers: { auth: token },
      });
      setRows(response.data.data || []);
      setRowCount(response.data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load inventory");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize]);

  React.useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  return (
    <ContentArea>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "#1E293B",
              fontFamily: "Kamerik",
              letterSpacing: "-1.5px",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Manage Inventory
          </Typography>
        </Box>

      <div style={{display:"flex", gap:"10px"}}>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefreshDispatch}
            disabled={refreshing}
            sx={{
              borderRadius: "12px",
              px: 3,
              bgcolor: "#2563EB",
              fontWeight: 700,
              height: "48px",
              "&:hover": { bgcolor: "#1D4ED8" },
              width: { xs: "100%", md: "auto" },
            }}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/AddCar")}
          sx={{
            borderRadius: "12px",
            px: 3,
            bgcolor: "#1E293B",
            fontWeight: 700,
            height: "48px",
            "&:hover": { bgcolor: "#0F172A" },
            width: { xs: "100%", md: "auto" },
          }}
        >
          Add Inventory
        </Button>
              </div>

      </Stack>

      <GlassCard elevation={0}>
        <StyledDataGrid
          autoHeight
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          loading={isLoading}
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          rowsPerPageOptions={[10]}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            "& .MuiDataGrid-toolbarContainer": {
              padding: "16px",
              gap: "12px",
              borderBottom: "1px solid #E2E8F0",
              "& .MuiButton-root": {
                color: "#64748B",
                fontWeight: 700,
                "&:hover": { color: "#1E293B" },
              },
            },
          }}
        />
      </GlassCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#1E293B" }}>
          Delete Inventory
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#475569" }}>
            Are you sure you want to delete this inventory item? This action
            cannot be undone and will permanently remove all associated details
            and images.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              borderRadius: "10px",
              color: "#64748B",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "10px",
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="bottom-right" />
    </ContentArea>
  );
};

export default InventoryPage;
